import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// ============================================================
// INTERFACES
// ============================================================

export interface Symptom {
  id: number;
  name: string;
  category: string;
}

export interface WomanProfile {
  id: number;
  age: number | null;
  lifecycle_stage: string;
  lifecycle_stage_display: string;
  created_at: string;
  updated_at: string;
}

export interface CycleEntry {
  id: number;
  avg_period_length: number;
  avg_cycle_length: number;
  last_period_start: string;
  flow_intensity: string;
  flow_intensity_display: string;
  symptoms: number[];
  symptom_details: { id: number; name: string }[];
  notes: string;
  next_period_start: string | null;
  next_period_end: string | null;
  ovulation_date: string | null;
  fertile_window_start: string | null;
  fertile_window_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface DailyLog {
  id: number;
  date: string;
  mood: string;
  mood_display: string;
  energy_level: string;
  energy_level_display: string;
  sleep_quality: string;
  sleep_quality_display: string;
  acne_level: string;
  acne_level_display: string;
  symptoms: number[];
  symptom_details: { id: number; name: string }[];
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PregnancyRecordCounts {
  week_logs: number;
  checkups: number;
  documents: number;
  kick_sessions: number;
  hospital_bag_checked: number;
  has_birth_plan: boolean;
}

export interface PregnancyProfile {
  id: number;
  lmp_date: string;
  edd: string | null;
  current_week: number | null;
  weeks_carried: number | null;
  delivery_type: string;
  delivery_type_display: string;
  status: string;
  status_display: string;
  delivery_date: string | null;
  outcome_notes: string;
  record_counts: PregnancyRecordCounts;
  postpartum_profile_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface PregnancyWeekLog {
  id: number;
  pregnancy: number;
  week_number: number;
  weight: number | null;
  mood: string;
  mood_display: string;
  fetal_movement_count: number | null;
  symptoms: number[];
  symptom_details: { id: number; name: string }[];
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PregnancyCheckup {
  id: number;
  pregnancy: number;
  checkup_type: string;
  checkup_type_display: string;
  date: string;
  doctor_name: string;
  hospital_name: string;
  notes: string;
  next_appointment: string | null;
  created_at: string;
  updated_at: string;
}

export interface PregnancyDocument {
  id: number;
  pregnancy: number;
  document_type: string;
  document_type_display: string;
  title: string;
  file: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface KickCounter {
  id: number;
  pregnancy: number;
  start_time: string;
  end_time: string | null;
  kick_count: number;
  duration_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export interface BirthPlan {
  id: number;
  pregnancy: number;
  delivery_preference: string;
  delivery_preference_display: string;
  pain_management: string;
  pain_management_display: string;
  hospital_name: string;
  doctor_name: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  special_requests: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PostpartumProfile {
  id: number;
  pregnancy: number | null;
  delivery_date: string;
  delivery_type_actual: string;
  delivery_type_actual_display: string;
  baby_weight_kg: number | null;
  recovery_mode: string;
  recovery_mode_display: string;
  status: string;
  status_display: string;
  completed_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecoveryLog {
  id: number;
  postpartum: number;
  date: string;
  bleeding_level: string;
  bleeding_level_display: string;
  pain_level: string;
  pain_level_display: string;
  incision_status: string;
  incision_status_display: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface MoodLog {
  id: number;
  postpartum: number;
  date: string;
  mood_score: number;
  screening_answers: Record<string, number>;
  screening_total: number | null;
  flagged: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface FeedingLog {
  id: number;
  postpartum: number;
  feeding_type: string;
  feeding_type_display: string;
  side: string;
  side_display: string;
  start_time: string;
  duration_minutes: number | null;
  volume_ml: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PostpartumExercise {
  id: number;
  postpartum: number;
  exercise_type: string;
  exercise_type_display: string;
  date: string;
  duration_minutes: number;
  completed: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface MenopauseProfile {
  id: number;
  stage: string;
  stage_display: string;
  onset_date: string | null;
  last_period_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface MenopauseSymptomLog {
  id: number;
  menopause_profile: number;
  date: string;
  hot_flash_count: number;
  hot_flash_severity: string;
  hot_flash_severity_display: string;
  mood: string;
  mood_display: string;
  sleep_quality: string;
  sleep_quality_display: string;
  joint_pain: string;
  joint_pain_display: string;
  fatigue_level: string;
  fatigue_level_display: string;
  weight: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface MenopauseWellnessSummary {
  menopause_profile: number;
  total_symptom_logs: number;
  recent_entry_count: number;
  average_hot_flashes_per_day: number;
  most_common_mood: {
    value: string;
    display: string;
    count: number;
  } | null;
  average_sleep_quality: {
    score: number | null;
    max_score: number;
    label: string | null;
    entry_count: number;
  };
  weight_trend: {
    latest_kg: number | null;
    first_kg: number | null;
    change_kg: number | null;
    direction: 'increased' | 'decreased' | 'stable' | 'unknown';
    record_count: number;
  };
}

export interface MenopauseMedication {
  id: number;
  menopause_profile: number;
  medication_type: string;
  medication_type_display: string;
  name: string;
  dosage: string;
  frequency: string;
  frequency_display: string;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface WHContent {
  id: number;
  lifecycle_stage: string;
  lifecycle_stage_display: string;
  content_type: string;
  content_type_display: string;
  title: string;
  content: string;
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface HospitalBagItem {
  id: number;
  pregnancy: number;
  item_key: string;
  checked: boolean;
  created_at: string;
  updated_at: string;
}

export interface AppointmentChecklistItem {
  id: number;
  menopause_profile: number;
  item_key: string;
  checked: boolean;
  created_at: string;
  updated_at: string;
}

export interface HydrationChecklistItem {
  id: number;
  menopause_profile: number;
  item_key: string;
  checked: boolean;
  created_at: string;
  updated_at: string;
}

// ------ Static reference data served from backend ------

export interface EPDSOption {
  value: number;
  label: string;
}

export interface EPDSQuestion {
  id: string;
  text: string;
  options: EPDSOption[];
}

export interface HospitalBagDefaultItem {
  item_key: string;
  label: string;
}

export interface HospitalBagColumn {
  title: string;
  items: HospitalBagDefaultItem[];
}

export interface AppointmentChecklistDefault {
  item_key: string;
  label: string;
}

export interface HydrationChecklistDefault {
  item_key: string;
  label: string;
}

// ============================================================
// HELPER
// ============================================================

const extractData = (res: any) => res.data?.results ?? res.data;
const extractSingle = (res: any) => res.data;

/**
 * Convert backend choices format {key: "Label"} to frontend format [{value, display_name}].
 * Also handles nested objects: {field_name: {key: "Label"}} -> {field_name: [{value, display_name}]}
 */
export const normalizeChoices = (data: any): Record<string, { value: string; display_name: string }[]> => {
  if (!data || typeof data !== 'object') return {};
  const result: Record<string, { value: string; display_name: string }[]> = {};
  for (const [field, val] of Object.entries(data)) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      result[field] = Object.entries(val as Record<string, string>).map(([k, v]) => ({
        value: k,
        display_name: String(v),
      }));
    } else if (Array.isArray(val)) {
      // Already in array format
      result[field] = val;
    }
  }
  return result;
};

// ============================================================
// SERVICE
// ============================================================

const womensHealthService = {
  // ------ Shared: Symptoms ------
  getSymptoms: async (): Promise<Symptom[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.symptoms)),
  getSymptomsByCategory: async (category: string): Promise<Symptom[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.symptomsByCategory, { params: { category } })),

  // ------ Shared: Woman Profile ------
  getProfiles: async (): Promise<WomanProfile[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.profiles)),
  createProfile: async (data: any): Promise<WomanProfile> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.profiles, data)),
  updateProfile: async (id: number, data: any): Promise<WomanProfile> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.profiles}${id}/`, data)),
  deleteProfile: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.profiles}${id}/`); },
  getProfileLatest: async (): Promise<WomanProfile | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.profiles}latest/`)); } catch { return null; } },
  getProfileChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.profiles}choices/`))),

  // ------ Stage 1: Cycles ------
  getCycles: async (): Promise<CycleEntry[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.cycles)),
  createCycle: async (data: any): Promise<CycleEntry> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.cycles, data)),
  updateCycle: async (id: number, data: any): Promise<CycleEntry> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.cycles}${id}/`, data)),
  deleteCycle: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.cycles}${id}/`); },
  getCycleLatest: async (): Promise<CycleEntry | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.cycles}latest/`)); } catch { return null; } },
  getCyclePredictions: async (id: number): Promise<CycleEntry> => extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.cycles}${id}/predictions/`)),
  getCycleChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.cycles}choices/`))),

  // ------ Stage 1: Daily Logs ------
  getDailyLogs: async (): Promise<DailyLog[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.dailyLogs)),
  createDailyLog: async (data: any): Promise<DailyLog> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.dailyLogs, data)),
  updateDailyLog: async (id: number, data: any): Promise<DailyLog> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.dailyLogs}${id}/`, data)),
  deleteDailyLog: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.dailyLogs}${id}/`); },
  getDailyLogByDate: async (date: string): Promise<DailyLog | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.dailyLogs}by_date/`, { params: { date } })); } catch { return null; } },
  getDailyLogLatest: async (): Promise<DailyLog | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.dailyLogs}latest/`)); } catch { return null; } },
  getDailyLogChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.dailyLogs}choices/`))),

  // ------ Stage 2: Pregnancy Profiles ------
  getPregnancyProfiles: async (): Promise<PregnancyProfile[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.pregnancyProfiles)),
  createPregnancyProfile: async (data: any): Promise<PregnancyProfile> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.pregnancyProfiles, data)),
  updatePregnancyProfile: async (id: number, data: any): Promise<PregnancyProfile> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.pregnancyProfiles}${id}/`, data)),
  deletePregnancyProfile: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.pregnancyProfiles}${id}/`); },
  getActivePregnancy: async (): Promise<PregnancyProfile | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.pregnancyProfiles}active/`)); } catch { return null; } },
  getPregnancyProfile: async (id: number): Promise<PregnancyProfile | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.pregnancyProfiles}${id}/`)); } catch { return null; } },
  deliverPregnancy: async (
    id: number,
    payload: { status: 'delivered' | 'loss'; delivery_date?: string; delivery_type?: string; outcome_notes?: string },
  ): Promise<PregnancyProfile> => extractSingle(await axiosInstance.post(`${API_ENDPOINTS.womensHealth.pregnancyProfiles}${id}/deliver/`, payload)),
  getPregnancyProfileChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.pregnancyProfiles}choices/`))),

  // ------ Stage 2: Pregnancy Week Logs ------
  getPregnancyWeekLogs: async (pregnancyId?: number): Promise<PregnancyWeekLog[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.pregnancyWeekLogs, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  createPregnancyWeekLog: async (data: any): Promise<PregnancyWeekLog> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.pregnancyWeekLogs, data)),
  updatePregnancyWeekLog: async (id: number, data: any): Promise<PregnancyWeekLog> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.pregnancyWeekLogs}${id}/`, data)),
  deletePregnancyWeekLog: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.pregnancyWeekLogs}${id}/`); },
  getPregnancyWeekLogChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.pregnancyWeekLogs}choices/`))),

  // ------ Stage 2: Pregnancy Checkups ------
  getPregnancyCheckups: async (pregnancyId?: number): Promise<PregnancyCheckup[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.pregnancyCheckups, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  createPregnancyCheckup: async (data: any): Promise<PregnancyCheckup> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.pregnancyCheckups, data)),
  updatePregnancyCheckup: async (id: number, data: any): Promise<PregnancyCheckup> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.pregnancyCheckups}${id}/`, data)),
  deletePregnancyCheckup: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.pregnancyCheckups}${id}/`); },
  getUpcomingCheckups: async (pregnancyId?: number): Promise<PregnancyCheckup[]> => extractData(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.pregnancyCheckups}upcoming/`, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  getPregnancyCheckupChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.pregnancyCheckups}choices/`))),

  // ------ Stage 2: Pregnancy Documents ------
  getPregnancyDocuments: async (pregnancyId?: number): Promise<PregnancyDocument[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.pregnancyDocuments, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  uploadPregnancyDocument: async (formData: FormData): Promise<PregnancyDocument> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.pregnancyDocuments, formData, { headers: { 'Content-Type': 'multipart/form-data' } })),
  deletePregnancyDocument: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.pregnancyDocuments}${id}/`); },

  // ------ Stage 2: Kick Counter ------
  getKickCounters: async (pregnancyId?: number): Promise<KickCounter[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.kickCounter, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  createKickCounter: async (data: any): Promise<KickCounter> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.kickCounter, data)),
  incrementKick: async (id: number): Promise<KickCounter> => extractSingle(await axiosInstance.post(`${API_ENDPOINTS.womensHealth.kickCounter}${id}/increment/`)),
  endKickSession: async (id: number): Promise<KickCounter> => extractSingle(await axiosInstance.post(`${API_ENDPOINTS.womensHealth.kickCounter}${id}/end-session/`)),
  getKickCounterLatest: async (pregnancyId?: number): Promise<KickCounter | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.kickCounter}latest/`, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })); } catch { return null; } },
  deleteKickCounter: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.kickCounter}${id}/`); },

  // ------ Stage 2: Birth Plan ------
  getBirthPlans: async (pregnancyId?: number): Promise<BirthPlan[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.birthPlan, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  createBirthPlan: async (data: any): Promise<BirthPlan> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.birthPlan, data)),
  updateBirthPlan: async (id: number, data: any): Promise<BirthPlan> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.birthPlan}${id}/`, data)),
  deleteBirthPlan: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.birthPlan}${id}/`); },
  getBirthPlanLatest: async (): Promise<BirthPlan | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.birthPlan}latest/`)); } catch { return null; } },
  getBirthPlanChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.birthPlan}choices/`))),

  // ------ Stage 3: Postpartum Profiles ------
  getPostpartumProfiles: async (): Promise<PostpartumProfile[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.postpartumProfiles)),
  createPostpartumProfile: async (data: any): Promise<PostpartumProfile> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.postpartumProfiles, data)),
  updatePostpartumProfile: async (id: number, data: any): Promise<PostpartumProfile> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.postpartumProfiles}${id}/`, data)),
  deletePostpartumProfile: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.postpartumProfiles}${id}/`); },
  getPostpartumProfileLatest: async (): Promise<PostpartumProfile | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.postpartumProfiles}latest/`)); } catch { return null; } },
  getActivePostpartum: async (): Promise<PostpartumProfile | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.postpartumProfiles}active/`)); } catch { return null; } },
  completePostpartum: async (id: number, completedDate?: string): Promise<PostpartumProfile> =>
    extractSingle(await axiosInstance.post(`${API_ENDPOINTS.womensHealth.postpartumProfiles}${id}/complete/`, completedDate ? { completed_date: completedDate } : {})),
  getPostpartumProfileChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.postpartumProfiles}choices/`))),

  // ------ Stage 3: Recovery Logs ------
  getRecoveryLogs: async (postpartumId?: number): Promise<RecoveryLog[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.recoveryLogs, { params: postpartumId ? { postpartum: postpartumId } : {} })),
  createRecoveryLog: async (data: any): Promise<RecoveryLog> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.recoveryLogs, data)),
  updateRecoveryLog: async (id: number, data: any): Promise<RecoveryLog> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.recoveryLogs}${id}/`, data)),
  deleteRecoveryLog: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.recoveryLogs}${id}/`); },
  getRecoveryLogChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.recoveryLogs}choices/`))),

  // ------ Stage 3: Mood Logs ------
  getMoodLogs: async (postpartumId?: number): Promise<MoodLog[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.moodLogs, { params: postpartumId ? { postpartum: postpartumId } : {} })),
  createMoodLog: async (data: any): Promise<MoodLog> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.moodLogs, data)),
  updateMoodLog: async (id: number, data: any): Promise<MoodLog> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.moodLogs}${id}/`, data)),
  deleteMoodLog: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.moodLogs}${id}/`); },
  getFlaggedMoodLogs: async (postpartumId?: number): Promise<MoodLog[]> => extractData(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.moodLogs}flagged_entries/`, { params: postpartumId ? { postpartum: postpartumId } : {} })),
  getMoodScreeningQuestions: async (): Promise<EPDSQuestion[]> => {
    const res = extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.moodLogs}screening-questions/`));
    return Array.isArray(res?.questions) ? res.questions : [];
  },

  // ------ Stage 3: Feeding Logs ------
  getFeedingLogs: async (postpartumId?: number): Promise<FeedingLog[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.feedingLogs, { params: postpartumId ? { postpartum: postpartumId } : {} })),
  createFeedingLog: async (data: any): Promise<FeedingLog> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.feedingLogs, data)),
  updateFeedingLog: async (id: number, data: any): Promise<FeedingLog> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.feedingLogs}${id}/`, data)),
  deleteFeedingLog: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.feedingLogs}${id}/`); },
  getTodayFeedingLogs: async (postpartumId?: number): Promise<FeedingLog[]> => extractData(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.feedingLogs}today/`, { params: postpartumId ? { postpartum: postpartumId } : {} })),
  getFeedingLogChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.feedingLogs}choices/`))),

  // ------ Stage 3: Postpartum Exercises ------
  getPostpartumExercises: async (postpartumId?: number): Promise<PostpartumExercise[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.postpartumExercises, { params: postpartumId ? { postpartum: postpartumId } : {} })),
  createPostpartumExercise: async (data: any): Promise<PostpartumExercise> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.postpartumExercises, data)),
  updatePostpartumExercise: async (id: number, data: any): Promise<PostpartumExercise> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.postpartumExercises}${id}/`, data)),
  deletePostpartumExercise: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.postpartumExercises}${id}/`); },
  getPostpartumExerciseChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.postpartumExercises}choices/`))),

  // ------ Stage 4: Menopause Profiles ------
  getMenopauseProfiles: async (): Promise<MenopauseProfile[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.menopauseProfiles)),
  createMenopauseProfile: async (data: any): Promise<MenopauseProfile> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.menopauseProfiles, data)),
  updateMenopauseProfile: async (id: number, data: any): Promise<MenopauseProfile> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.menopauseProfiles}${id}/`, data)),
  deleteMenopauseProfile: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.menopauseProfiles}${id}/`); },
  getMenopauseProfileLatest: async (): Promise<MenopauseProfile | null> => { try { return extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.menopauseProfiles}latest/`)); } catch { return null; } },
  getMenopauseProfileChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.menopauseProfiles}choices/`))),

  // ------ Stage 4: Menopause Symptom Logs ------
  getMenopauseSymptomLogs: async (profileId?: number): Promise<MenopauseSymptomLog[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.menopauseSymptomLogs, { params: profileId ? { menopause_profile: profileId } : {} })),
  getMenopauseWellnessSummary: async (profileId: number): Promise<MenopauseWellnessSummary> => extractSingle(await axiosInstance.get(API_ENDPOINTS.womensHealth.menopauseSymptomLogSummary, { params: { menopause_profile: profileId } })),
  createMenopauseSymptomLog: async (data: any): Promise<MenopauseSymptomLog> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.menopauseSymptomLogs, data)),
  updateMenopauseSymptomLog: async (id: number, data: any): Promise<MenopauseSymptomLog> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.menopauseSymptomLogs}${id}/`, data)),
  deleteMenopauseSymptomLog: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.menopauseSymptomLogs}${id}/`); },
  getMenopauseSymptomLogChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.menopauseSymptomLogs}choices/`))),

  // ------ Stage 4: Menopause Medications ------
  getMenopauseMedications: async (profileId?: number): Promise<MenopauseMedication[]> => extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.menopauseMedications, { params: profileId ? { menopause_profile: profileId } : {} })),
  createMenopauseMedication: async (data: any): Promise<MenopauseMedication> => extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.menopauseMedications, data)),
  updateMenopauseMedication: async (id: number, data: any): Promise<MenopauseMedication> => extractSingle(await axiosInstance.patch(`${API_ENDPOINTS.womensHealth.menopauseMedications}${id}/`, data)),
  deleteMenopauseMedication: async (id: number): Promise<void> => { await axiosInstance.delete(`${API_ENDPOINTS.womensHealth.menopauseMedications}${id}/`); },
  getActiveMenopauseMedications: async (profileId?: number): Promise<MenopauseMedication[]> => extractData(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.menopauseMedications}active_medications/`, { params: profileId ? { menopause_profile: profileId } : {} })),
  getMenopauseMedicationChoices: async () => normalizeChoices(extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.menopauseMedications}choices/`))),

  // ------ CMS: WHContent (per stage + content_type) ------
  getContent: async (lifecycle_stage: string, content_type?: string): Promise<WHContent[]> => {
    const params: any = { lifecycle_stage };
    if (content_type) params.content_type = content_type;
    return extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.content, { params }));
  },

  // ------ Stage 2: Hospital Bag (per active pregnancy) ------
  getHospitalBag: async (pregnancyId?: number): Promise<HospitalBagItem[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.hospitalBag, { params: pregnancyId ? { pregnancy: pregnancyId } : {} })),
  saveHospitalBagItem: async (data: { pregnancy: number; item_key: string; checked: boolean }): Promise<HospitalBagItem> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.hospitalBag, data)),
  getHospitalBagDefaults: async (): Promise<HospitalBagColumn[]> => {
    const res = extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.hospitalBag}defaults/`));
    return Array.isArray(res?.columns) ? res.columns : [];
  },

  // ------ Stage 4: Appointment Preparation Checklist (per menopause profile) ------
  getAppointmentChecklist: async (profileId?: number): Promise<AppointmentChecklistItem[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.appointmentChecklist, { params: profileId ? { menopause_profile: profileId } : {} })),
  saveAppointmentChecklistItem: async (data: { menopause_profile: number; item_key: string; checked: boolean }): Promise<AppointmentChecklistItem> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.appointmentChecklist, data)),
  getAppointmentChecklistDefaults: async (): Promise<AppointmentChecklistDefault[]> => {
    const res = extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.appointmentChecklist}defaults/`));
    return Array.isArray(res?.items) ? res.items : [];
  },

  // ------ Stage 4: Daily Hydration & Wellness Checklist (per menopause profile) ------
  getHydrationChecklist: async (profileId?: number): Promise<HydrationChecklistItem[]> =>
    extractData(await axiosInstance.get(API_ENDPOINTS.womensHealth.hydrationChecklist, { params: profileId ? { menopause_profile: profileId } : {} })),
  saveHydrationChecklistItem: async (data: { menopause_profile: number; item_key: string; checked: boolean }): Promise<HydrationChecklistItem> =>
    extractSingle(await axiosInstance.post(API_ENDPOINTS.womensHealth.hydrationChecklist, data)),
  getHydrationChecklistDefaults: async (): Promise<HydrationChecklistDefault[]> => {
    const res = extractSingle(await axiosInstance.get(`${API_ENDPOINTS.womensHealth.hydrationChecklist}defaults/`));
    return Array.isArray(res?.items) ? res.items : [];
  },
};

export default womensHealthService;
